import type { PerspectiveCamera } from 'three';
import type { CameraControlsInstance } from '../../../../utils/lazy-camera-controls';

export interface ViewportAnchor {
	vertical?: number;
	horizontal?: number;
}

export interface ComputeFocalOffsetFromAnchorParams {
	anchor: ViewportAnchor;
	camera: PerspectiveCamera;
	controls: CameraControlsInstance;
}

/**
 * Convert a viewport anchor (0-1 normalized coordinates) to world-space focal offset.
 *
 * The calculation uses perspective projection to determine how much world-space
 * offset is needed to shift the scene to the desired viewport position.
 *
 * @param anchor - Normalized viewport position (0.5 = center, 0.33 = bottom third)
 * @param camera - The perspective camera
 * @param controls - Camera controls instance (for current distance)
 * @returns World-space focal offset { x, y }
 */
export const computeFocalOffsetFromAnchorCfn = ({
	anchor,
	camera,
	controls
}: ComputeFocalOffsetFromAnchorParams): { x: number; y: number } => {
	// Get current camera distance from controls
	const distance = (controls as any).distance;
	if (!Number.isFinite(distance) || distance <= 0) {
		return { x: 0, y: 0 };
	}

	// Compute world extents at current distance
	const fovRadians = (camera.fov * Math.PI) / 180;
	const worldHeight = 2 * distance * Math.tan(fovRadians / 2);
	const worldWidth = worldHeight * camera.aspect;

	// Calculate offsets from center (0.5)
	// anchor.vertical: 0 = bottom, 0.5 = center, 1 = top
	// anchor.horizontal: 0 = left, 0.5 = center, 1 = right
	const verticalAnchor = anchor.vertical ?? 0.5;
	const horizontalAnchor = anchor.horizontal ?? 0.5;

	// Convert anchor to offset:
	// - If anchor is 0.33 (bottom third), we need to shift UP so scene appears lower
	// - offsetY = worldHeight * (0.5 - anchor.vertical)
	// - When anchor < 0.5, offset is positive (shift up in world space)
	const offsetY = worldHeight * (0.5 - verticalAnchor);

	// For horizontal: anchor < 0.5 means shift right (positive X in world)
	const offsetX = worldWidth * (0.5 - horizontalAnchor);

	return { x: offsetX, y: offsetY };
};
